import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useAuth } from '../../../AuthContext';
import { fetchData } from '../../../apiService';
import './AccountHeadModal.css';

const SalesExecutiveModal = ({
  open,
  onClose,
  modalAction,
  selectedItem,
  formData,
  setFormData,
  onSave,
  salesExecutives = [],
  balanceSheetHeads = [],
  loading,
  viewSlabs = [],
  categories = [],
  items = [],
}) => {
  const { companyId, token } = useAuth();
  const [errors, setErrors] = useState({});

  // Branch-wise opening balance states
  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [apiOpeningBalances, setApiOpeningBalances] = useState(null);

  const today = new Date().toISOString().split('T')[0];

  // Fetch branches when modal opens
  useEffect(() => {
    if (open && companyId && token) {
      fetchBranches();
    } else if (!open) {
      setBranches([]);
      setApiOpeningBalances(null);
    }
  }, [open, companyId, token]);

  useEffect(() => {
    if (selectedItem && modalAction !== 'create') {
      setApiOpeningBalances(selectedItem.OpeningBalances || []);
      setFormData({ ...selectedItem, OpeningBalances: [] });
    } else if (modalAction === 'create') {
      setApiOpeningBalances(null);
      setFormData({
        Name: '',
        BalanceSheetHeadId: null,
        IsPayableOrReceivable: false,
        IsLoginNeeded: false,
        IsActive: true,
        SalesTargetAmount: 0,
        OpeningBalances: []
      });
    }
  }, [selectedItem, modalAction]);

  const fetchBranches = async () => {
    setBranchesLoading(true);
    try {
      const response = await fetchData('BranchApi', `/${companyId}`, token);
      const data = response.response_data || response || [];
      setBranches(Array.isArray(data) ? data : [data]);
    } catch (error) {
      console.error('Error fetching branches:', error);
      setBranches([]);
    }
    setBranchesLoading(false);
  };

  // Merge branches with API opening balances once both are ready
  useEffect(() => {
    if (branches.length === 0) return;

    if (modalAction === 'create') {
      setFormData(prev => ({
        ...prev,
        OpeningBalances: branches.map(b => ({
          BranchId: b.Id,
          BranchName: b.Name || b.BranchName || '',
          OpeningBalance: 0,
          OpeningDate: today,
          IsPayableOrReceivable: false
        }))
      }));
    } else if ((modalAction === 'edit' || modalAction === 'view') && apiOpeningBalances !== null) {
      const merged = branches.map(b => {
        const found = apiOpeningBalances.find(r => r.BranchId === b.Id);
        return found
          ? { ...found, Id: found.Id || 0, BranchName: b.Name || b.BranchName || found.BranchName || '' }
          : {
              Id: 0,
              BranchId: b.Id,
              BranchName: b.Name || b.BranchName || '',
              OpeningBalance: 0,
              OpeningDate: today,
              IsPayableOrReceivable: false
            };
      });
      setFormData(prev => ({ ...prev, OpeningBalances: merged }));
    }
  }, [branches, modalAction, apiOpeningBalances]);

  const handleOpeningBalanceChange = (branchId, field, value) => {
    setFormData(prev => ({
      ...prev,
      OpeningBalances: (prev.OpeningBalances || []).map(row =>
        row.BranchId === branchId ? { ...row, [field]: value } : row
      )
    }));
  };

  // ── Duplicate detection ──────────────────────────────────────────────────
  // Block if another sales executive with the same Name already exists.
  const isDuplicate = useMemo(() => {
    if (modalAction === 'view') return false;
    const currentName = (formData.Name || '').trim().toLowerCase();
    if (!currentName) return false;
    const currentId = selectedItem?.MasterId || selectedItem?.Id || 0;
    return salesExecutives.some(exec => {
      const execId = exec.MasterId || exec.Id || 0;
      if (modalAction === 'edit' && execId === currentId) return false;
      return (exec.Name || '').trim().toLowerCase() === currentName;
    });
  }, [formData.Name, salesExecutives, modalAction, selectedItem]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.Name?.toString().trim()) newErrors.Name = 'Name is required';
    if (!formData.BalanceSheetHeadId) newErrors.BalanceSheetHeadId = 'Balance Sheet Head is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleSave = () => {
    if (isDuplicate) return;
    if (validateForm()) onSave();
  };

  const getTitle = () => {
    const map = { create: 'New', edit: 'Edit', view: 'View' };
    return `${map[modalAction] || 'New'} Sales Executive`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth className="account-head-modal">
      <DialogTitle sx={{ p: 0 }}>
        <div className="modal-title-container">
          <div className="modal-title-text">
            <h2>{getTitle()}</h2>
          </div>
          <Button onClick={onClose} className="modal-close-btn">
            <Close />
          </Button>
        </div>
      </DialogTitle>

      <DialogContent>
        <div className="form-container">

          {/* Basic Information */}
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e3a8a', mb: 0.5, mt: 0.5, fontSize: '0.75rem' }}>
            Basic Information
          </Typography>

          <div className="form-row">
            <div className="form-field required-field">
              <TextField
                fullWidth
                label="Name *"
                placeholder="Enter name"
                value={formData.Name || ''}
                onChange={(e) => handleInputChange('Name', e.target.value)}
                error={!!errors.Name || isDuplicate}
                helperText={errors.Name || (isDuplicate ? 'A sales executive with this name already exists' : '')}
                disabled={modalAction === 'view'}
                required
              />
            </div>
          </div>

          {/* Duplicate warning */}
          {isDuplicate && (
            <Alert severity="error" sx={{ mb: 1, fontSize: '0.8rem', py: 0.5 }}>
              Duplicate Entry: A sales executive named <strong>"{(formData.Name || '').trim()}"</strong> already exists.
            </Alert>
          )}

          {/* Classification */}
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e3a8a', mb: 0.5, mt: 1, fontSize: '0.75rem' }}>
            Classification
          </Typography>

          <div className="form-row">
            <div className="form-field">
              <FormControl fullWidth error={!!errors.BalanceSheetHeadId}>
                <InputLabel>Balance Sheet Head *</InputLabel>
                <Select
                  value={formData.BalanceSheetHeadId || ''}
                  onChange={(e) => handleInputChange('BalanceSheetHeadId', e.target.value)}
                  disabled={modalAction === 'view'}
                  label="Balance Sheet Head *"
                >
                  <MenuItem value="">Select Balance Sheet Head</MenuItem>
                  {balanceSheetHeads.map(head => (
                    <MenuItem key={head.Id} value={head.Id}>{head.Name}</MenuItem>
                  ))}
                </Select>
                {errors.BalanceSheetHeadId && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>{errors.BalanceSheetHeadId}</Typography>
                )}
              </FormControl>
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <TextField
                fullWidth
                label="Sales Target Amount (₹)"
                placeholder="Enter sales target amount"
                type="number"
                value={formData.SalesTargetAmount ?? ''}
                onChange={(e) => handleInputChange('SalesTargetAmount', parseFloat(e.target.value) || 0)}
                disabled={modalAction === 'view'}
                inputProps={{ min: 0, step: '0.01' }}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.IsPayableOrReceivable ?? false}
                    onChange={(e) => handleInputChange('IsPayableOrReceivable', e.target.checked)}
                    disabled={modalAction === 'view'}
                  />
                }
                label="Payable/Receivable"
              />
            </div>
            <div className="form-field">
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.IsLoginNeeded === true}
                    onChange={(e) => handleInputChange('IsLoginNeeded', e.target.checked)}
                    disabled={modalAction === 'view'}
                  />
                }
                label="Is Login Needed"
              />
            </div>
            {/* <div className="form-field">
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.IsActive ?? true}
                    onChange={(e) => handleInputChange('IsActive', e.target.checked)}
                    disabled={modalAction === 'view'}
                  />
                }
                label="Active"
              />
            </div> */}
          </div>


          {/* Opening Balances (Branch-wise) */}
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e3a8a', mb: 0.5, mt: 1, fontSize: '0.75rem' }}>
            Opening Balances (Branch-wise)
          </Typography>
          <Box sx={{ mb: 0.75 }}>
            {branchesLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                <CircularProgress size={14} />
                <span style={{ fontSize: '0.72rem', color: '#64748b' }}>Loading branches...</span>
              </Box>
            ) : (
              <Box sx={{ border: '1px solid #e5e7eb', borderRadius: '6px', overflow: 'hidden' }}>
                <Table size="small" sx={{ tableLayout: 'fixed' }}>
                  <TableHead>
                    <TableRow sx={{ background: '#f1f5f9' }}>
                      <TableCell sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#1e3a8a', py: 0.5, px: 1, width: '28%' }}>Branch</TableCell>
                      <TableCell sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#1e3a8a', py: 0.5, px: 1, width: '28%' }}>Opening Balance (₹)</TableCell>
                      <TableCell sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#1e3a8a', py: 0.5, px: 1, width: '26%' }}>Opening Date</TableCell>
                      <TableCell sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#1e3a8a', py: 0.5, px: 1, width: '18%', textAlign: 'center' }}>Payable</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(formData.OpeningBalances || []).map((row, idx) => (
                      <TableRow key={row.BranchId} sx={{ background: idx % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                        <TableCell sx={{ fontSize: '0.72rem', py: 0.4, px: 1, color: '#374151', fontWeight: 500 }}>
                          {row.BranchName}
                        </TableCell>
                        <TableCell sx={{ py: 0.4, px: 1 }}>
                          <TextField
                            size="small"
                            type="number"
                            value={row.OpeningBalance ?? 0}
                            onChange={(e) => handleOpeningBalanceChange(row.BranchId, 'OpeningBalance', parseFloat(e.target.value) || 0)}
                            disabled={modalAction === 'view'}
                            inputProps={{ min: 0, style: { padding: '3px 6px', fontSize: '0.72rem' } }}
                            sx={{ '& .MuiOutlinedInput-root': { minHeight: '26px' }, width: '100%' }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 0.4, px: 1 }}>
                          <TextField
                            size="small"
                            type="date"
                            value={row.OpeningDate?.split('T')[0] || ''}
                            onChange={(e) => handleOpeningBalanceChange(row.BranchId, 'OpeningDate', e.target.value)}
                            disabled={modalAction === 'view'}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ style: { padding: '3px 6px', fontSize: '0.72rem' } }}
                            sx={{ '& .MuiOutlinedInput-root': { minHeight: '26px' }, width: '100%' }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 0.4, px: 1, textAlign: 'center' }}>
                          <Switch
                            size="small"
                            checked={row.IsPayableOrReceivable ?? false}
                            onChange={(e) => handleOpeningBalanceChange(row.BranchId, 'IsPayableOrReceivable', e.target.checked)}
                            disabled={modalAction === 'view'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                    {(formData.OpeningBalances || []).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ fontSize: '0.72rem', color: '#9ca3af', textAlign: 'center', py: 1 }}>
                          No branches available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Box>

          {/* Commission Slabs — view mode only */}
          {modalAction === 'view' && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e3a8a', mb: 1, fontSize: '0.75rem' }}>
                Commission Slabs
              </Typography>
              {viewSlabs.length === 0 ? (
                <Typography sx={{ fontSize: '12px', color: '#999', textAlign: 'center', py: 2 }}>
                  No commission slabs configured.
                </Typography>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ background: '#2C3E6E' }}>
                        {[['Category', '120px'], ['Item', '130px'], ['Based On', '80px'], ['Commission %', '100px'], ['On Payment', '90px'], ['Due Days', '70px'], ['Remarks', ''], ['Active', '60px']].map(([label, w]) => (
                          <TableCell key={label} style={{ color: 'white', fontSize: '11px', fontWeight: 600, padding: '5px 8px', whiteSpace: 'nowrap', width: w || 'auto' }}>
                            {label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {viewSlabs.map((s, idx) => (
                        <TableRow key={s.Id} style={{ background: idx % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                          <TableCell style={{ fontSize: '12px', padding: '5px 8px' }}>
                            {categories.find(c => c.Id === s.CategoryId)?.Name || '— All —'}
                          </TableCell>
                          <TableCell style={{ fontSize: '12px', padding: '5px 8px' }}>
                            {items.find(i => i.Id === s.ItemId)?.Name || '— All —'}
                          </TableCell>
                          <TableCell style={{ fontSize: '12px', padding: '5px 8px' }}>
                            {s.IsBasedOnQuantityOrAmount ? 'Qty' : 'Amt'}
                          </TableCell>
                          <TableCell style={{ fontSize: '12px', padding: '5px 8px', textAlign: 'right' }}>
                            {s.CommissionRate ?? 0}%
                          </TableCell>
                          <TableCell style={{ fontSize: '12px', padding: '5px 8px', textAlign: 'center' }}>
                            {s.IsBasedOnPayment ? 'Yes' : 'No'}
                          </TableCell>
                          <TableCell style={{ fontSize: '12px', padding: '5px 8px', textAlign: 'right' }}>
                            {s.DueDays ?? 0}
                          </TableCell>
                          <TableCell style={{ fontSize: '12px', padding: '5px 8px' }}>
                            {s.Remarks || '-'}
                          </TableCell>
                          <TableCell style={{ fontSize: '12px', padding: '5px 8px', textAlign: 'center' }}>
                            <Chip label={s.IsActive ? 'Active' : 'Inactive'} color={s.IsActive ? 'success' : 'default'} size="small" sx={{ fontSize: '0.6rem', height: '18px' }} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Box>
          )}

        </div>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">Cancel</Button>
        {modalAction !== 'view' && !isDuplicate && (
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            {modalAction === 'create' ? 'Add Executive' : 'Update'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SalesExecutiveModal;

